import React, { useState, useRef, useLayoutEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Checkbox from "components/CheckboxCustom";
import { useTranslation } from "contexts/Safra/TranslationContext";
import {
    FilterOverlay,
    FilterContainer,
    FilterSection,
    FilterTitle,
    CheckboxGroup,
    FilterFooter,
    FilterHeading,
    SectionStack,
    PriceGrid,
    PriceInput,
    InputFloatingContainer,
    FilterActions,
    ApplyButton,
    ClearButton,
    CloseButton
} from "components/Safra/FilterAirfare/styles";

export default function PassageFilter() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { initialFilters, airlines = [] } = location.state || {};

    const [stops, setStops] = useState(initialFilters?.stops || []);
    const [selectedAirlines, setSelectedAirlines] = useState(initialFilters?.airlines || []);
    const [baggage, setBaggage] = useState(initialFilters?.baggage || "all");
    const [priceMin, setPriceMin] = useState(initialFilters?.priceMin || "");
    const [priceMax, setPriceMax] = useState(initialFilters?.priceMax || "");
    const [outboundTimes, setOutboundTimes] = useState(initialFilters?.outboundTimes || []);
    const [inboundTimes, setInboundTimes] = useState(initialFilters?.inboundTimes || []);

    const priceMinRef = useRef(null);
    const priceMaxRef = useRef(null);
    const [minCursor, setMinCursor] = useState(null);
    const [maxCursor, setMaxCursor] = useState(null);

    useLayoutEffect(() => {
        if (priceMinRef.current && minCursor !== null) {
            priceMinRef.current.setSelectionRange(minCursor, minCursor);
        }
    }, [priceMin, minCursor]);

    useLayoutEffect(() => {
        if (priceMaxRef.current && maxCursor !== null) {
            priceMaxRef.current.setSelectionRange(maxCursor, maxCursor);
        }
    }, [priceMax, maxCursor]);

    const formatNumberWithPts = (numStr) => {
        if (!numStr) return '';
        const parts = numStr.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return `${parts.join('.')} Pts`;
    };

    const createPriceHandler = (currentValue, setter, cursorSetter) => (e) => {
        const { value, selectionStart } = e.target;
        const oldFormattedValue = formatNumberWithPts(currentValue);
        const oldSeparatorCount = (oldFormattedValue.substring(0, selectionStart).match(/\./g) || []).length;
        const rawValue = value.replace(/[^0-9]/g, '');
        setter(rawValue);
        const newFormattedValue = formatNumberWithPts(rawValue);
        const newSeparatorCount = (newFormattedValue.substring(0, selectionStart).match(/\./g) || []).length;
        const cursorCorrection = newSeparatorCount - oldSeparatorCount;
        let newCursorPos = selectionStart + cursorCorrection;
        if (rawValue.length < currentValue.length && newCursorPos > newFormattedValue.length - 4) {
             newCursorPos = newFormattedValue.length - 4;
        }
        if (newCursorPos < 0) newCursorPos = 0;
        cursorSetter(newCursorPos);
    };

    const handleStopsChange = (stopValue) => {
        if (stops.includes(stopValue)) {
            setStops(stops.filter((s) => s !== stopValue));
        } else {
            setStops([...stops, stopValue]);
        }
    };

    const handleAirlineChange = (airline) => {
        if (selectedAirlines.includes(airline)) {
            setSelectedAirlines(selectedAirlines.filter((a) => a !== airline));
        } else {
            setSelectedAirlines([...selectedAirlines, airline]);
        }
    };

    const handleTimeToggle = (value, type) => {
        const current = type === "outbound" ? outboundTimes : inboundTimes;
        const updated = current.includes(value)
            ? current.filter((item) => item !== value)
            : [...current, value];
        if (type === "outbound") setOutboundTimes(updated);
        else setInboundTimes(updated);
    };

    const onClose = () => navigate(-1);

    const handleClear = () => {
        setBaggage("all");
        setPriceMin("");
        setPriceMax("");
        setStops([]);
        setSelectedAirlines([]);
        setOutboundTimes([]);
        setInboundTimes([]);
    };

    const handleApply = () => {
        const appliedFilters = {
            stops,
            airlines: selectedAirlines,
            baggage,
            priceMin,
            priceMax,
            outboundTimes,
            inboundTimes
        };
        navigate("/passage-search-result", { state: { appliedFilters } });
    };

    return (
        <FilterOverlay>
            <FilterContainer>
                <CloseButton type="button" onClick={onClose} aria-label={t('safra.common.close')}>
                    ×
                </CloseButton>
                <FilterHeading>{t('safra.common.filter_page.title')}</FilterHeading>

                <SectionStack>
                    <FilterSection>
                        <FilterTitle>{t('safra.common.filter_page.baggage.title')}</FilterTitle>
                        <CheckboxGroup>
                            <Checkbox
                                type="radio"
                                id="filter-baggage-all"
                                name="baggage-filter"
                                label={t('safra.common.filter_page.baggage.all')}
                                checked={baggage === "all"}
                                onChange={() => setBaggage("all")}
                                customRadioStyle={true}
                            />
                            <Checkbox
                                type="radio"
                                id="filter-baggage-checked"
                                name="baggage-filter"
                                label={t('safra.common.filter_page.baggage.checked')}
                                checked={baggage === "checked"}
                                onChange={() => setBaggage("checked")}
                                customRadioStyle={true}
                            />
                            <Checkbox
                                type="radio"
                                id="filter-baggage-hand"
                                name="baggage-filter"
                                label={t('safra.common.filter_page.baggage.hand')}
                                checked={baggage === "hand"}
                                onChange={() => setBaggage("hand")}
                                customRadioStyle={true}
                            />
                        </CheckboxGroup>
                    </FilterSection>

                    <FilterSection>
                        <FilterTitle>{t('safra.common.filter_page.price_points')}</FilterTitle>
                            <PriceGrid>
                                <InputFloatingContainer>
                                    <PriceInput
                                        ref={priceMinRef}
                                        type="text"
                                        id="priceMin"
                                        placeholder=" "
                                        value={formatNumberWithPts(priceMin)}
                                        onChange={createPriceHandler(priceMin, setPriceMin, setMinCursor)}
                                        aria-label={t('safra.common.filter_page.min_price')}
                                        autocomplete="off"
                                    />
                                    <label htmlFor="priceMin">{t('safra.common.filter_page.min_price')}</label>
                                </InputFloatingContainer>
                                <InputFloatingContainer>
                                    <PriceInput
                                        ref={priceMaxRef}
                                        type="text"
                                        id="priceMax"
                                        placeholder=" "
                                        value={formatNumberWithPts(priceMax)}
                                        onChange={createPriceHandler(priceMax, setPriceMax, setMaxCursor)}
                                        aria-label={t('safra.common.filter_page.max_price')}
                                        autocomplete="off"
                                    />
                                    <label htmlFor="priceMax">{t('safra.common.filter_page.max_price')}</label>
                                </InputFloatingContainer>
                            </PriceGrid>
                    </FilterSection>

                    <FilterSection>
                        <FilterTitle>{t('safra.common.filter_page.stops.title')}</FilterTitle>
                        <CheckboxGroup>
                            <Checkbox
                                id="filter-stop-all"
                                label={t('safra.common.filter_page.stops.all')}
                                checked={stops.length === 0}
                                onChange={() => setStops([])}
                            />
                            <Checkbox
                                id="filter-stop-direct"
                                label={t('safra.common.filter_page.stops.direct')}
                                checked={stops.includes(0)}
                                onChange={() => handleStopsChange(0)}
                            />
                        </CheckboxGroup>
                    </FilterSection>

                    <FilterSection>
                        <FilterTitle>{t('safra.common.filter_page.airlines')}</FilterTitle>
                        <CheckboxGroup>
                            <Checkbox
                                id="filter-airline-all"
                                label={t('safra.common.filter_page.airlines_all')}
                                checked={selectedAirlines.length === 0}
                                onChange={() => setSelectedAirlines([])}
                            />
                            {airlines.map((airline) => (
                                <Checkbox
                                    key={airline.code}
                                    id={`filter-airline-${airline.code}`}
                                    label={airline.name}
                                    checked={selectedAirlines.includes(airline.code)}
                                    onChange={() => handleAirlineChange(airline.code)}
                                />
                            ))}
                        </CheckboxGroup>
                    </FilterSection>

                    <FilterSection>
                        <FilterTitle>{t('safra.common.filter_page.times.outbound')}</FilterTitle>
                        <CheckboxGroup>
                            <Checkbox
                                id="filter-outbound-morning"
                                label={t('safra.common.filter_page.times.morning')}
                                checked={outboundTimes.includes("morning")}
                                onChange={() => handleTimeToggle("morning", "outbound")}
                            />
                            <Checkbox
                                id="filter-outbound-afternoon"
                                label={t('safra.common.filter_page.times.afternoon')}
                                checked={outboundTimes.includes("afternoon")}
                                onChange={() => handleTimeToggle("afternoon", "outbound")}
                            />
                            <Checkbox
                                id="filter-outbound-night"
                                label={t('safra.common.filter_page.times.night')}
                                checked={outboundTimes.includes("night")}
                                onChange={() => handleTimeToggle("night", "outbound")}
                            />
                        </CheckboxGroup>
                    </FilterSection>

                    <FilterSection>
                        <FilterTitle>{t('safra.common.filter_page.times.inbound')}</FilterTitle>
                        <CheckboxGroup>
                            <Checkbox
                                id="filter-inbound-morning"
                                label={t('safra.common.filter_page.times.morning')}
                                checked={inboundTimes.includes("morning")}
                                onChange={() => handleTimeToggle("morning", "inbound")}
                            />
                            <Checkbox
                                id="filter-inbound-afternoon"
                                label={t('safra.common.filter_page.times.afternoon')}
                                checked={inboundTimes.includes("afternoon")}
                                onChange={() => handleTimeToggle("afternoon", "inbound")}
                            />
                            <Checkbox
                                id="filter-inbound-night"
                                label={t('safra.common.filter_page.times.night')}
                                checked={inboundTimes.includes("night")}
                                onChange={() => handleTimeToggle("night", "inbound")}
                            />
                        </CheckboxGroup>
                    </FilterSection>
                </SectionStack>

                <FilterFooter>
                    <FilterActions>
                        <ClearButton type="button" onClick={handleClear} aria-label={t('safra.common.filter_page.cta_clear')}>
                            {t('safra.common.filter_page.cta_clear').toUpperCase()}
                        </ClearButton>
                        <ApplyButton type="button" onClick={handleApply} aria-label={t('safra.common.filter_page.cta_apply')}>
                            {t('safra.common.filter_page.cta_apply').toUpperCase()}
                        </ApplyButton>
                    </FilterActions>
                </FilterFooter>
            </FilterContainer>
        </FilterOverlay>
    );
}
